import { type ReactiveVar, useReactiveVar } from "@apollo/client";

import { getPersistedExperimentOverrides } from "@shared/hooks/usePersistedExperimentOverrides/getPersistedExperimentOverrides";
import { makeFSVar } from "@shared/reactiveVar/makeFSVar";
import {
  isDevelopment,
  isE2E,
  isProduction,
  isStaging,
} from "@shared/utils/fullscriptEnv/fullscriptEnv";

type OverrideValues = Record<string, string>;
type Experiments = Record<string, readonly string[]>;

type ExperimentVar = {
  roleId?: string;
  userId?: string;
  storeId?: string;
  analyticsAnonymousId?: string;
  currentExperiments?: Experiments;
  overrideValues?: OverrideValues;
  cookie?: string;
};

type ExperimentContextData = Pick<
  ExperimentVar,
  "userId" | "roleId" | "storeId" | "analyticsAnonymousId" | "overrideValues" | "cookie"
>;

type SetOverrideParams = {
  experimentName: string;
  group: string;
  isAdminImpersonating: boolean;
};

type SetCurrentExperimentsParams = {
  experimentName: string;
  groups: readonly string[];
  isAdminImpersonating: boolean;
};

type UseExperimentVar = ExperimentVar & {
  setOverride: ({ experimentName, group }: SetOverrideParams) => void;
  setCurrentExperiments: ({ experimentName, groups }: SetCurrentExperimentsParams) => void;
};

const INITIAL_VALUES = {
  roleId: undefined,
  userId: undefined,
  storeId: undefined,
  analyticsAnonymousId: undefined,
  overrideValues: {},
  currentExperiments: {},
};

let experimentVar: ReactiveVar<ExperimentVar> | undefined;

const getInitialOverrideValues = (cookie?: string) => {
  if (!isDevelopment() && !isStaging() && !isE2E()) return {};

  return getPersistedExperimentOverrides(cookie);
};

/**
 * Used to reset overrides for all experiments to their initial values
 */
const clearOverrides = () => {
  if (!experimentVar) return;

  const rest = experimentVar();
  experimentVar({ ...rest, overrideValues: {} });
};

const clearExperimentVar = () => {
  experimentVar = undefined;
};

/**
 * Initialize the experimentVar from useExperimentVar with some initial value.
 * Typically only used in Providers or entrypoints of applications
 * Subsequent calls will have no affect after this has been called once before
 *
 * @param experimentVarParams - The initial value for useExperimentVar
 */
const createExperimentVar = (experimentVarParams: ExperimentContextData = {}) => {
  if (experimentVar) return experimentVar;

  const { cookie, ...params } = experimentVarParams;
  const overrideValues = getInitialOverrideValues(cookie);

  experimentVar ||= makeFSVar<ExperimentVar>(
    {
      ...INITIAL_VALUES,
      overrideValues,
      ...params,
    },
    "experimentVar",
    clearExperimentVar
  );
};

/**
 * Used to force/override an experiment to be the specified group
 *
 * @param experimentName - The experiment to override
 * @param group - The group to be part of for the specified experiment ex: "control" or "experiment"
 */
const setOverride: UseExperimentVar["setOverride"] = ({
  experimentName,
  group,
  isAdminImpersonating,
}) => {
  if ((isProduction() && !isAdminImpersonating) || !experimentVar) return;

  const { overrideValues, ...rest } = experimentVar();

  if (overrideValues?.[experimentName] === group) return;

  experimentVar({ ...rest, overrideValues: { ...overrideValues, [experimentName]: group } });
};

/**
 * Used to specify groups for an experiment, only to be used via useExperiment, not to be used directly
 *
 * @param experimentName - The experiment to create
 * @param groups - Groups for said experiment
 */
const setCurrentExperiments: UseExperimentVar["setCurrentExperiments"] = ({
  experimentName,
  groups,
  isAdminImpersonating,
}) => {
  if ((isProduction() && !isAdminImpersonating) || !experimentVar) return;

  const { currentExperiments, ...rest } = experimentVar();

  if (currentExperiments?.[experimentName]) return;

  experimentVar({
    ...rest,
    currentExperiments: { ...currentExperiments, [experimentName]: groups },
  });
};

/**
 * Hook that provides global access to experiment data.
 * Gives you access to userId, storeId, roleId, currentExperiment and any overrides
 */
const useExperimentVar = (): UseExperimentVar => {
  // initialize with default values if not done already
  createExperimentVar();

  // experimentVar is guaranteed to be present as it's initialized in createExperimentVar above
  const reactiveVar = useReactiveVar(experimentVar!);

  return { ...reactiveVar, setOverride, setCurrentExperiments };
};

export {
  useExperimentVar,
  createExperimentVar,
  clearOverrides,
  clearExperimentVar,
  type ExperimentContextData,
  type OverrideValues,
};
